import { useCallback, useMemo } from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { UserProductTipKey } from "@common/schemas/user.schema";
import { useSetUserProductTipMutation } from "@api/user.api";
import { useConstant } from "@hooks/useConstant";
import { useUser } from "./useUser";
import { useTypedFlags } from "./useTypedFlags";

export const useProductTipBanner = (key: UserProductTipKey) => {
    const user = useUser();
    const { productTipBanners } = useTypedFlags();
    const setProductTipMutation = useSetUserProductTipMutation();
    const today = useConstant(() => new Date());

    const showProductTipBanner = useMemo(() => {
        if (!user || !productTipBanners) {
            return false;
        }

        const userCreatedAt =
            typeof user.createdAt === "string"
                ? parseISO(user.createdAt)
                : new Date(user.createdAt);
        const userAgeInDays = differenceInCalendarDays(today, userCreatedAt);
        const isUserEligible = userAgeInDays >= 0 && userAgeInDays <= 30;
        const hasSeenTip = Boolean(user.productTips?.[key]);

        return isUserEligible && !hasSeenTip;
    }, [key, productTipBanners, user, today]);

    const dismissBanner = useCallback(() => {
        if (setProductTipMutation.isPending) {
            return;
        }

        setProductTipMutation.mutate({ key, value: new Date().toISOString() });
    }, [key, setProductTipMutation]);

    return {
        showProductTipBanner,
        dismissProductTipBanner: dismissBanner,
        isDismissingProductTipBanner: setProductTipMutation.isPending,
    };
};
